import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowRight, Share2, Loader2, TrendingUp, Sparkles, Mail, X, Check, AlertCircle, Camera, Zap, Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import DeviceAutocomplete from "./components/DeviceAutocomplete";
import AdminDashboard from "./pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Apple vs Orange image for incomparable items
const APPLES_ORANGES_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8006c961-850d-4a1f-bac2-67c5a9810285/images/a2783b5d5cee54c79e41fee9b2e3a4a9bb44d518aacb89b0191384c250e913a3.png";

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Dynamic SEO component
const DynamicSEO = ({ comparison }) => {
  if (!comparison) {
    return (
      <Helmet>
        <title>Do I Need an Upgrade? | Free Device Comparison Tool</title>
        <meta name="description" content="Compare any two electronic devices instantly with AI-powered analysis. Get detailed comparisons for phones, laptops, tablets & more. Free upgrade recommendations!" />
      </Helmet>
    );
  }

  const title = `${comparison.device1} vs ${comparison.device2} - Comparison | Do I Need an Upgrade?`;
  const description = `Compare ${comparison.device1} vs ${comparison.device2}. Upgrade score: ${comparison.rating}/10. ${comparison.summary}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

function HomePage() {
  const [device1, setDevice1] = useState("");
  const [device2, setDevice2] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popularComparisons, setPopularComparisons] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharingLoading, setSharingLoading] = useState(false);
  const sessionStartTime = useRef(Date.now());

  // Track visitor and session time
  useEffect(() => {
    const visitorId = getVisitorId();
    
    // Track visit
    axios.post(`${API}/analytics/track-visit`, { visitor_id: visitorId })
      .catch(err => console.log("Tracking error:", err));
    
    // Update session duration periodically
    const updateSession = () => {
      const durationSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      axios.post(`${API}/analytics/update-session`, { 
        visitor_id: visitorId, 
        duration_seconds: durationSeconds 
      }).catch(err => console.log("Session update error:", err));
    };

    // Update every 30 seconds
    const intervalId = setInterval(updateSession, 30000);
    
    // Update on page unload
    const handleUnload = () => updateSession();
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleUnload);
      updateSession();
    };
  }, []);

  // Fetch popular comparisons
  const fetchPopular = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/popular`);
      setPopularComparisons(response.data);
    } catch (error) {
      console.error("Failed to fetch popular comparisons:", error);
    }
  }, []);

  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);

  // Handle comparison
  const handleCompare = async () => {
    if (!device1.trim() || !device2.trim()) {
      toast.error("Please enter both devices to compare");
      return;
    }

    setLoading(true);
    setComparison(null);

    try {
      const response = await axios.post(`${API}/compare`, {
        device1: device1.trim(),
        device2: device2.trim()
      });
      setComparison(response.data);
      fetchPopular(); // Refresh popular after new comparison
    } catch (error) {
      console.error("Comparison failed:", error);
      toast.error("Failed to compare devices. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle share
  const handleShare = async () => {
    if (!shareEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setSharingLoading(true);

    try {
      await axios.post(`${API}/share`, {
        comparison_id: comparison.id,
        recipient_email: shareEmail.trim()
      });
      toast.success(`Comparison shared to ${shareEmail}`);
      setShareModalOpen(false);
      setShareEmail("");
    } catch (error) {
      console.error("Share failed:", error);
      toast.error("Failed to share comparison. Please try again.");
    } finally {
      setSharingLoading(false);
    }
  };

  // Handle popular comparison click
  const handlePopularClick = (device1Name, device2Name) => {
    setDevice1(device1Name);
    setDevice2(device2Name);
    setComparison(null);
  };

  // Get rating color based on score
  const getRatingColor = (rating) => {
    if (rating >= 8) return "text-emerald-500";
    if (rating >= 5) return "text-amber-500";
    return "text-zinc-500";
  };

  // Get rating background color
  const getRatingBgColor = (rating) => {
    if (rating >= 8) return "bg-emerald-50 border-emerald-100";
    if (rating >= 5) return "bg-amber-50 border-amber-100";
    return "bg-zinc-50 border-zinc-100";
  };

  return (
    <div className="App min-h-screen bg-white">
      <DynamicSEO comparison={comparison} />
      
      {/* Header */}
      <header className="py-6 px-6 border-b border-zinc-100" role="banner">
        <nav className="max-w-5xl mx-auto flex items-center justify-between" aria-label="Main navigation">
          <a 
            href="/"
            className="font-outfit text-xl font-medium text-zinc-950 cursor-pointer no-underline"
            onClick={(e) => {
              e.preventDefault();
              setComparison(null);
              setDevice1("");
              setDevice2("");
            }}
            data-testid="logo-home-link"
            aria-label="Do I Need an Upgrade - Home"
          >
            doineedanupgrade<span className="text-zinc-400">.com</span>
          </a>
        </nav>
      </header>

      <main className="py-16 md:py-24 px-6" role="main">
        <article className="max-w-5xl mx-auto" itemScope itemType="https://schema.org/WebApplication">
          
          {/* Hero Section */}
          <header className="text-center mb-12 md:mb-16 animate-fadeIn">
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium text-zinc-950 tracking-tight mb-4" itemProp="name">
              Do I need an Upgrade?
            </h1>
            <p className="font-manrope text-base md:text-lg text-zinc-500 max-w-2xl mx-auto" itemProp="description">
              Compare any two electronic devices and get an AI-powered recommendation on whether it's time to upgrade.
            </p>
          </header>

          {/* Comparison Input Section */}
          <section className="relative flex flex-col md:flex-row items-stretch gap-4 md:gap-6 mb-12 animate-fadeIn-delay-1" aria-label="Device comparison form">
            {/* Device 1 Input */}
            <DeviceAutocomplete
              id="device1-input"
              value={device1}
              onChange={(e) => setDevice1(e.target.value)}
              placeholder="e.g., iPhone 15 Pro"
              data-testid="device1-input"
              onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
              label="Current Device"
            />

            {/* VS Badge */}
            <div className="hidden md:flex items-center justify-center relative" aria-hidden="true">
              <div className="vs-badge">VS</div>
            </div>
            <div className="md:hidden flex justify-center -my-2" aria-hidden="true">
              <div className="bg-white border-4 border-zinc-100 text-zinc-400 font-outfit font-bold rounded-full w-12 h-12 flex items-center justify-center text-sm shadow-sm">
                VS
              </div>
            </div>

            {/* Device 2 Input */}
            <DeviceAutocomplete
              id="device2-input"
              value={device2}
              onChange={(e) => setDevice2(e.target.value)}
              placeholder="e.g., iPhone 16 Pro"
              data-testid="device2-input"
              onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
              label="New Device"
            />
          </section>

          {/* Compare Button */}
          <div className="flex justify-center mb-16 animate-fadeIn-delay-2">
            <button
              onClick={handleCompare}
              disabled={loading || !device1.trim() || !device2.trim()}
              className="btn-primary flex items-center gap-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="compare-button"
              aria-label="Compare the two devices"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" aria-hidden="true" />
                  <span>Compare Devices</span>
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </>
              )}
            </button>
          </div>

          {/* Results Section */}
          {comparison && (
            <div className="animate-fadeIn">
              {!comparison.comparable ? (
                /* Not Comparable State */
                <div className="card-default text-center py-12" data-testid="incomparable-result">
                  <img 
                    src={APPLES_ORANGES_IMAGE} 
                    alt="Apple and Orange" 
                    className="w-48 h-48 mx-auto mb-6 object-contain"
                  />
                  <h3 className="font-outfit text-2xl md:text-3xl font-medium text-zinc-950 mb-3">
                    You can't compare apples to oranges
                  </h3>
                  <p className="font-manrope text-zinc-500 max-w-md mx-auto">
                    These devices are too different to compare meaningfully. Try comparing devices in the same category.
                  </p>
                </div>
              ) : (
                /* Comparison Results */
                <div className="card-default" data-testid="comparison-result">
                  {/* Centered Header */}
                  <div className="text-center mb-10">
                    <h3 className="font-outfit text-3xl md:text-4xl font-medium text-zinc-950 mb-4">
                      {comparison.device1} <span className="text-zinc-300">vs</span> {comparison.device2}
                    </h3>
                    <p className="font-manrope text-zinc-500 text-lg max-w-xl mx-auto leading-relaxed">
                      {comparison.summary}
                    </p>
                    <button
                      onClick={() => setShareModalOpen(true)}
                      className="mt-6 inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors font-manrope text-sm"
                      data-testid="share-button"
                    >
                      <Share2 className="w-4 h-4" />
                      Share this comparison
                    </button>
                  </div>

                  {/* Key Highlights - 3 Metrics */}
                  {comparison.key_highlights && comparison.key_highlights.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-testid="key-highlights">
                      {comparison.key_highlights.map((highlight, index) => {
                        const isPositive = ["better", "faster"].includes(highlight.verdict.toLowerCase());
                        const isNeutral = highlight.verdict.toLowerCase() === "same";
                        const icon = highlight.metric.toLowerCase().includes("camera") || highlight.metric.toLowerCase().includes("photo")
                          ? <Camera className="w-5 h-5" />
                          : highlight.metric.toLowerCase().includes("speed")
                          ? <Zap className="w-5 h-5" />
                          : <Monitor className="w-5 h-5" />;
                        
                        return (
                          <div
                            key={index}
                            className={`rounded-2xl border p-5 text-center transition-all ${
                              isPositive 
                                ? "bg-emerald-50 border-emerald-200" 
                                : isNeutral 
                                ? "bg-zinc-50 border-zinc-200" 
                                : "bg-red-50 border-red-200"
                            }`}
                            data-testid={`key-highlight-${index}`}
                          >
                            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-3 ${
                              isPositive ? "bg-emerald-100 text-emerald-600" : isNeutral ? "bg-zinc-200 text-zinc-600" : "bg-red-100 text-red-600"
                            }`}>
                              {icon}
                            </div>
                            <div className="font-outfit text-sm font-medium text-zinc-500 uppercase tracking-wider mb-1">
                              {highlight.metric}
                            </div>
                            <div className={`font-outfit text-xl font-bold mb-1 ${
                              isPositive ? "text-emerald-600" : isNeutral ? "text-zinc-600" : "text-red-600"
                            }`}>
                              {highlight.verdict}
                            </div>
                            <p className="font-manrope text-xs text-zinc-500 leading-relaxed">
                              {highlight.detail}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Device Images */}
                  <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-10">
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden bg-zinc-50 border border-zinc-100 shadow-sm mb-3">
                        <img 
                          src={comparison.device1_image} 
                          alt={comparison.device1}
                          className="w-full h-full object-cover"
                          data-testid="device1-image"
                          onError={(e) => {
                            e.target.src = "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=400&fit=crop";
                          }}
                        />
                      </div>
                      <span className="font-outfit text-sm font-medium text-zinc-700">{comparison.device1}</span>
                    </div>
                    
                    <div className="text-zinc-300 font-outfit font-bold text-2xl hidden md:block">VS</div>
                    
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden bg-zinc-50 border border-zinc-100 shadow-sm mb-3">
                        <img 
                          src={comparison.device2_image} 
                          alt={comparison.device2}
                          className="w-full h-full object-cover"
                          data-testid="device2-image"
                          onError={(e) => {
                            e.target.src = "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=400&fit=crop";
                          }}
                        />
                      </div>
                      <span className="font-outfit text-sm font-medium text-zinc-700">{comparison.device2}</span>
                    </div>
                  </div>

                  {/* Score Card */}
                  <div className={`score-card mb-8 ${getRatingBgColor(comparison.rating)}`} data-testid="score-card">
                    <span className="label-style mb-2">Upgrade Score</span>
                    <span className={`font-outfit text-6xl md:text-7xl font-bold ${getRatingColor(comparison.rating)}`} data-testid="rating-score">
                      {comparison.rating}
                    </span>
                    <span className="font-outfit text-2xl text-zinc-400 mb-4">/10</span>
                    
                    {comparison.rating >= 8 ? (
                      <div className="flex items-center gap-2 text-emerald-600 font-medium" data-testid="upgrade-recommended">
                        <TrendingUp className="w-5 h-5" />
                        <span>Upgrade Recommended</span>
                      </div>
                    ) : comparison.rating >= 5 ? (
                      <div className="flex items-center gap-2 text-amber-600 font-medium" data-testid="consider-upgrade">
                        <AlertCircle className="w-5 h-5" />
                        <span>Consider Upgrading</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-zinc-500 font-medium" data-testid="no-upgrade-needed">
                        <Check className="w-5 h-5" />
                        <span>No Upgrade Needed</span>
                      </div>
                    )}
                    
                    <p className="font-manrope text-zinc-600 mt-3 text-sm max-w-lg">
                      {comparison.recommendation}
                    </p>
                  </div>

                  {/* Comparison Points */}
                  <div data-testid="comparison-points">
                    <h4 className="font-outfit text-lg font-medium text-zinc-950 mb-4">Comparison Details</h4>
                    
                    {/* Table Header */}
                    <div className="grid grid-cols-3 gap-4 pb-3 border-b-2 border-zinc-950 mb-2">
                      <span className="font-outfit text-sm font-medium text-zinc-500">Category</span>
                      <span className="font-outfit text-sm font-medium text-zinc-950">{comparison.device1}</span>
                      <span className="font-outfit text-sm font-medium text-zinc-950">{comparison.device2}</span>
                    </div>

                    {/* Table Rows */}
                    {comparison.comparison_points.map((point, index) => (
                      <div key={index} className="comparison-row" data-testid={`comparison-point-${index}`}>
                        <span className="font-manrope text-zinc-500">{point.category}</span>
                        <span className={`font-manrope ${point.winner === 'device1' ? 'text-emerald-600 font-medium' : 'text-zinc-700'}`}>
                          {point.device1_value}
                          {point.winner === 'device1' && <Check className="inline w-4 h-4 ml-1" />}
                        </span>
                        <span className={`font-manrope ${point.winner === 'device2' ? 'text-emerald-600 font-medium' : 'text-zinc-700'}`}>
                          {point.device2_value}
                          {point.winner === 'device2' && <Check className="inline w-4 h-4 ml-1" />}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Popular Comparisons Section */}
          {popularComparisons.length > 0 && !comparison && (
            <section className="mt-16 animate-fadeIn-delay-3" data-testid="popular-comparisons" aria-label="Popular device comparisons">
              <h2 className="font-outfit text-xl font-medium text-zinc-950 mb-6 text-center">
                Popular Comparisons
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="list">
                {popularComparisons.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handlePopularClick(item.device1, item.device2)}
                    className="bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 rounded-2xl p-5 text-left transition-all hover:-translate-y-1 group"
                    data-testid={`popular-comparison-${index}`}
                    aria-label={`Compare ${item.device1} vs ${item.device2}`}
                    role="listitem"
                  >
                    <div className="font-outfit text-zinc-950 font-medium mb-1 group-hover:text-zinc-700">
                      {item.device1}
                    </div>
                    <div className="text-zinc-400 text-sm mb-1" aria-hidden="true">vs</div>
                    <div className="font-outfit text-zinc-950 font-medium group-hover:text-zinc-700">
                      {item.device2}
                    </div>
                    <div className="mt-3 text-xs text-zinc-400">
                      {item.count} comparison{item.count !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-100" role="contentinfo">
        <div className="max-w-5xl mx-auto text-center">
          <p className="font-manrope text-sm text-zinc-400">
            AI-powered device comparison tool. Compare phones, laptops, tablets & more. Results are for reference only.
          </p>
          <p className="font-manrope text-xs text-zinc-300 mt-2">
            © Powered by KB Labs. Free device comparison tool.
          </p>
        </div>
      </footer>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit">Share Comparison</DialogTitle>
            <DialogDescription className="font-manrope">
              Send this comparison to someone via email.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-zinc-400" />
              <Input
                type="email"
                placeholder="Enter email address"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="flex-1"
                data-testid="share-email-input"
                onKeyDown={(e) => e.key === 'Enter' && handleShare()}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShareModalOpen(false)}
                data-testid="share-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleShare}
                disabled={sharingLoading || !shareEmail.trim()}
                className="bg-zinc-950 hover:bg-zinc-800"
                data-testid="share-send-button"
              >
                {sharingLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
